<template>
  <div>
    <!-- Filter Controls: Client + Campaign + Generate button -->
    <div class="border border-gray-200 rounded-md bg-gray-50 p-3 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 mb-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">Filters</span>
        <div class="flex justify-center gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-medium text-gray-600">Scope:</span>
            <label class="flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="dashboardScope" value="outreach" class="w-3 h-3">
              <span class="text-xs text-gray-600">Outreach Drafts</span>
            </label>
            <label class="flex items-center gap-1 cursor-pointer">
              <input type="radio" v-model="dashboardScope" value="crm" class="w-3 h-3">
              <span class="text-xs text-gray-600">CRM Actions</span>
            </label>
          </div>
        </div>
        <div class="flex justify-end">
          <button
            v-if="hasActiveFilters"
            type="button"
            @click="clearFilters"
            class="text-xs text-indigo-600 hover:text-indigo-800"
          >Clear all</button>
        </div>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <div class="flex flex-col gap-1 min-w-0">
          <label class="text-xs font-medium text-gray-600">Client</label>
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.client.options"
              :default-value="filters.client.value"
              select-multiple
              placeholder="Search clients…"
              no-selection-message="All clients"
              @update-value="onFilterChange('client', $event)"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1 min-w-0">
          <label class="text-xs font-medium text-gray-600">Campaign</label>
          <div class="filter-typeahead w-full">
            <TypeAheadMultiSelectBox
              :options="filters.campaign.options"
              :default-value="filters.campaign.value"
              select-multiple
              placeholder="Search campaigns…"
              no-selection-message="All active campaigns"
              @update-value="onFilterChange('campaign', $event)"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600 invisible">Generate</label>
          <button
            type="button"
            @click="generateDashboard"
            :disabled="isLoading"
            class="h-8 px-3 rounded-md text-sm bg-dark-primary text-white hover:opacity-90 disabled:opacity-40 whitespace-nowrap"
          >{{ isLoading ? 'Loading…' : 'Generate' }}</button>
        </div>
      </div>
    </div>

    <!-- Summary Cards: Company Metrics (Left) & Emails Sent (Right) -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <!-- Left Card: Total Companies, Contacted, Not Interested/Pass, Responded (2x2 Grid) -->
      <div class="border border-gray-200 rounded-xl bg-white p-5">
        <div v-if="dashboardLoading" class="text-gray-400 text-sm py-6 text-center">Loading…</div>
        <div v-else-if="dashboardCompanies" class="grid grid-cols-2 border border-gray-100 divide-x divide-y divide-gray-100">
          <!-- Upper Left: Total Companies -->
          <div class="p-4 flex flex-col justify-center cursor-pointer hover:ring-1 hover:ring-indigo-300 hover:ring-inset transition-colors" @click="openCompanyDetailsModal('total')">
            <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Total Companies</div>
            <div class="text-3xl font-semibold">{{ totalCompanies }}</div>
          </div>

          <!-- Upper Right: Companies Contacted -->
          <div class="p-4 flex flex-col justify-center cursor-pointer hover:ring-1 hover:ring-indigo-300 hover:ring-inset transition-colors" @click="openCompanyDetailsModal('contacted')">
            <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Companies Contacted</div>
            <div class="text-3xl font-semibold">{{ companiesContacted }}</div>
          </div>

          <!-- Lower Left: Not Interested/Pass -->
          <div class="p-4 flex flex-col justify-center cursor-pointer hover:ring-1 hover:ring-indigo-300 hover:ring-inset transition-colors" @click="openCompanyDetailsModal('not_interested')">
            <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Not Interested/Pass</div>
            <div class="text-3xl font-semibold">{{ notInterestedCount }}</div>
            <div v-if="notInterestedRatePct !== null" class="text-xs text-gray-500 mt-1">
              {{ notInterestedRatePct }}% of {{ notInterestedBase }}
              {{ dashboardScope === 'outreach' ? 'contacted' : 'companies' }}
            </div>
          </div>

          <!-- Lower Right: Companies Responded -->
          <div class="p-4 flex flex-col justify-center cursor-pointer hover:ring-1 hover:ring-indigo-300 hover:ring-inset transition-colors" @click="openCompanyDetailsModal('responded')">
            <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Companies Responded</div>
            <div class="text-3xl font-semibold">{{ companiesResponded }}</div>
            <div v-if="respondedRatePct !== null" class="text-xs text-gray-500 mt-1">
              {{ respondedRatePct }}% response rate · of {{ companiesContacted }} contacted
            </div>
          </div>
        </div>
        <div v-else class="text-gray-400 text-sm py-6 text-center">
          Please select a client/campaign and generate.
        </div>
      </div>

      <!-- Right Card: Emails Sent Chart -->
      <div class="border border-gray-200 rounded-xl bg-white p-5">
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Emails Sent</div>
        <div v-if="emailsSentLoading" class="text-gray-400 text-sm py-6 text-center">Loading…</div>
        <div v-else-if="emailsSentSummary" class="h-48">
          <CRMDoughnutChart :data="emailsSentChartData" :options="emailsSentChartOptions" />
        </div>
        <div v-else class="text-gray-400 text-sm py-6 text-center">
          Please select a client/campaign and generate.
        </div>
      </div>
    </div>

    <!-- Next Actions & Overdue Section -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
      <!-- Next Actions by Type Chart -->
      <div class="sm:col-span-2 border border-gray-200 rounded-xl bg-white p-5 min-h-60">
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-4">
          Next Actions by Type
          <span v-if="actionsData?.open_actions" class="text-gray-400 font-normal text-[10px] ml-2">{{ openActionsTotal }} total · {{ totalCompanies }} companies</span>
        </div>
        <div v-if="actionsLoading" class="text-gray-400 text-sm py-6 text-center">Loading…</div>
        <div v-else-if="actionsData?.open_actions && actionsData.open_actions.length > 0" class="w-full">
          <svg id="next-actions-chart" viewBox="0 0 640 340" width="100%" style="height: auto; min-height: 220px; display: block;"></svg>
        </div>
        <div v-else class="text-gray-400 text-sm py-6 text-center">
          No open next actions
        </div>
      </div>

      <!-- Overdue Next Actions Tile -->
      <div class="border border-gray-200 rounded-xl bg-white p-5 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-300 transition-colors" @click="showOverdueModal = true">
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Overdue Next Actions</div>
        <div v-if="actionsLoading" class="text-gray-400 text-sm">Loading…</div>
        <div v-else class="text-4xl font-semibold text-gray-800">{{ overdueActionsCount }}</div>
        <div class="text-xs text-gray-500 mt-2">Click to view & filter</div>
      </div>
    </div>

    <!-- Trend Chart Section -->
    <div class="mt-6 border-t border-gray-200 pt-6">
      <h3 class="text-sm font-semibold text-gray-900 mb-4">Trend</h3>

      <!-- Trend Filters -->
      <div class="border border-gray-200 rounded-md bg-gray-50 p-3 mb-4">
        <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Start date</label>
            <input type="date" v-model="trendStartDate" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">End date</label>
            <input type="date" v-model="trendEndDate" :max="todayStr" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Interval</label>
            <select v-model="trendInterval" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option>Day</option>
              <option>Week</option>
              <option>Month</option>
            </select>
          </div>
          <div class="flex flex-col gap-1 relative" data-trend-status-filter>
            <label class="text-xs font-medium text-gray-600">Status</label>
            <button type="button" @click="toggleStatusFilter" class="border border-gray-300 rounded-md px-2 py-1 text-sm text-left bg-white flex justify-between items-center">
              <span class="text-xs">{{ statusFilterSummary }}</span>
              <span class="text-xs">&#9662;</span>
            </button>
            <div v-if="showStatusFilterMenu" class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-max">
              <label v-for="status in stackingOrder" :key="status" class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                <input type="checkbox" :value="status" v-model="activeStatuses" class="w-4 h-4">
                {{ status }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Stacked Step Area Chart -->
      <div class="border border-gray-200 rounded-xl bg-white p-5">
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-4">Company Status Over Time</div>
        <div v-if="trendChartLoading" class="text-gray-400 text-sm py-10 text-center">Loading…</div>
        <div v-else-if="trendPeriods && trendPeriods.length > 0" class="w-full">
          <svg id="trend-area-chart" viewBox="0 0 800 320" width="100%" style="height: auto; display: block; overflow: visible;"></svg>
          <div class="flex flex-row flex-wrap justify-start gap-4 mt-4 pt-4 border-t border-gray-100">
            <div v-for="status in activeStatuses" :key="status" class="flex items-center gap-2">
              <span class="inline-block w-3 h-3 rounded-full" :style="{ backgroundColor: statusColors[status] }"></span>
              <span class="text-xs text-gray-700">{{ status }}</span>
            </div>
          </div>
        </div>
        <div v-else class="text-gray-400 text-sm py-10 text-center">
          Select dates to view trend data
        </div>
      </div>
    </div>

    <!-- Email Flow Sankey Chart -->
    <div class="mt-6 border border-gray-200 rounded-xl bg-white p-5">
      <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-4">Email Flow</div>
      <div v-if="emailFlowLoading" class="text-gray-400 text-sm py-10 text-center">Loading…</div>
      <div v-else-if="emailFlowEdges && emailFlowEdges.length > 0">
        <OutreachEmailFlowSankey
          :edges="emailFlowEdges"
          :node-colors="emailFlowNodeColors"
        />
      </div>
      <div v-else class="text-gray-400 text-sm py-10 text-center">
        No email flow data for this campaign.
      </div>
    </div>

    <!-- Overdue Actions Modal -->
    <div v-if="showOverdueModal" class="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div class="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 class="text-lg font-semibold">Overdue Next Actions</h2>
          <button type="button" @click="showOverdueModal = false" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div class="p-5 border-b border-gray-200 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Company</label>
            <select v-model="overdueCompanyFilter" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option value="">All</option>
              <option v-for="name in uniqueOverdueCompanies" :key="name" :value="name">{{ name }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Next Action</label>
            <select v-model="overdueActionFilter" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option value="">All</option>
              <option v-for="action in uniqueOverdueActions" :key="action" :value="action">{{ action }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Sort by</label>
            <select v-model="overdueSortBy" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option value="days_overdue_desc">Most Overdue</option>
              <option value="days_overdue_asc">Least Overdue</option>
              <option value="company_name_asc">Company Name (A–Z)</option>
              <option value="company_name_desc">Company Name (Z–A)</option>
            </select>
          </div>
        </div>
        <div class="h-96 overflow-y-auto border-t border-gray-200">
          <table class="w-full text-sm table-fixed">
            <thead class="sticky top-0 bg-white border-b border-gray-200">
              <tr class="text-left">
                <th class="w-2/5 p-3 font-semibold">Company</th>
                <th class="w-2/5 p-3 font-semibold">Next Action</th>
                <th class="w-1/5 p-3 font-semibold text-center">Days Overdue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredOverdueActions.length === 0" class="border-b border-gray-100">
                <td colspan="3" class="py-6 text-center text-gray-400">No overdue actions</td>
              </tr>
              <tr v-for="action in filteredOverdueActions" :key="`${action.company_id}-${action.action}`" class="border-b border-gray-100 hover:bg-gray-50">
                <td class="w-2/5 px-3 py-2">{{ action.company_name }}</td>
                <td class="w-2/5 px-3 py-2">{{ action.action }}</td>
                <td class="w-1/5 px-3 py-2 text-center font-semibold">{{ action.days_overdue }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Company Details Modal -->
    <div v-if="showCompanyDetailsModal" class="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div class="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 class="text-lg font-semibold">{{ bucketTitle }} ({{ bucketCount }})</h2>
          <button type="button" @click="showCompanyDetailsModal = false" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div class="p-5 border-b border-gray-200 grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Company</label>
            <input type="text" v-model="companyDetailsFilter" placeholder="Search companies…" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Sort by</label>
            <select v-model="companyDetailsSortBy" class="border border-gray-300 rounded-md px-2 py-1 text-sm">
              <option value="company_name_asc">Company Name (A–Z)</option>
              <option value="company_name_desc">Company Name (Z–A)</option>
            </select>
          </div>
        </div>
        <div class="h-96 overflow-y-auto border-t border-gray-200">
          <table class="w-full text-sm table-fixed">
            <thead class="sticky top-0 bg-white border-b border-gray-200">
              <tr class="text-left">
                <th class="p-3 font-semibold">Company Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="companiesForModal.length === 0" class="border-b border-gray-100">
                <td class="py-6 text-center text-gray-400">No companies</td>
              </tr>
              <tr v-for="company in companiesForModal" :key="company.id" class="border-b border-gray-100 hover:bg-gray-50">
                <td class="px-3 py-2">{{ company.company_name }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { getOutreachDashboardCompanies, getOutreachDashboardEmailsSent, getOutreachDashboardActions, getOutreachDashboardCRMActionHistory, getOutreachDashboardEmailFlow, getOutreachDashboardCompaniesOutreachScoped, getOutreachDashboardEmailsSentOutreachScoped } from '@/services/Company'
import CRMDoughnutChart from '@/components/crm-reporting/CRMDoughnutChart.vue'
import OutreachEmailFlowSankey from '@/components/outreach-drafts/OutreachEmailFlowSankey.vue'
import TypeAheadMultiSelectBox from '@/components/base-components/TypeAheadMultiSelectBox.vue'

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update-filter'])

const store = useStore()
const $toast = useToast()

// DASHBOARD STATE
const dashboardFilters = reactive({ client: [], campaign: [] })
const dashboardLoading = ref(false)
const dashboardScope = ref('outreach')
const dashboardCompanies = ref(null)

// EMAILS SENT STATE
const emailsSentLoading = ref(false)
const emailsSentSummary = ref(null)

// NEXT ACTIONS STATE
const actionsLoading = ref(false)
const actionsData = ref(null)

// EMAIL FLOW STATE
const emailFlowLoading = ref(false)
const emailFlowEdges = ref(null)

// OVERDUE ACTIONS MODAL STATE
const showOverdueModal = ref(false)
const overdueCompanyFilter = ref('')
const overdueActionFilter = ref('')
const overdueSortBy = ref('days_overdue_desc')

// COMPANY DETAILS MODAL STATE
const showCompanyDetailsModal = ref(false)
const companyDetailsBucket = ref(null)
const companyDetailsFilter = ref('')
const companyDetailsSortBy = ref('company_name_asc')

// TREND CHART STATE
const trendChartLoading = ref(false)
const trendStartDate = ref(null)
const trendEndDate = ref(null)
const trendInterval = ref('Week')
const trendPeriods = ref([])
const trendCompanyData = ref([])
const showStatusFilterMenu = ref(false)

// CONSTANTS: EMAIL STAGES
const EMAIL_STAGES = [
  { name: 'Email 1', color: '#86b6ef' },
  { name: 'Email 2', color: '#5598e7' },
  { name: 'Email 3', color: '#2a78d6' },
  { name: 'Email 4', color: '#1c5cab' },
  { name: 'Email Sent', color: '#d1d5db' },
]

// CONSTANTS: TREND STATUSES
const stackingOrder = ['Not Contacted', 'Email 1', 'Email 2', 'Email 3', 'Email 4', 'Responded', 'Not Interested']
const activeStatuses = ref([...stackingOrder])

// Emit filter change event for parent to handle
function onFilterChange(key, selectedIds) {
  emit('update-filter', { key, value: selectedIds })
}

function clearFilters() {
  emit('update-filter', { key: 'client', value: [] })
  emit('update-filter', { key: 'campaign', value: [] })
}

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return props.filters.client.value.length > 0 || props.filters.campaign.value.length > 0
})

function dashboardParams() {
  return { campaign: dashboardFilters.campaign, client_drf: dashboardFilters.client }
}

async function generateDashboard() {
  dashboardFilters.client = [...props.filters.client.value]
  dashboardFilters.campaign = [...props.filters.campaign.value]
  dashboardCompanies.value = null
  emailsSentSummary.value = null
  actionsData.value = null
  emailFlowEdges.value = null

  if (!dashboardFilters.client.length && !dashboardFilters.campaign.length) return

  fetchDashboardCompanies()
  fetchEmailsSent()
  fetchActions()
  fetchEmailFlow()
  fetchTrendData()
}

async function fetchDashboardCompanies() {
  dashboardLoading.value = true
  try {
    if (dashboardScope.value === 'outreach') {
      dashboardCompanies.value = await getOutreachDashboardCompaniesOutreachScoped(store.state.auth.token, dashboardParams())
    } else {
      dashboardCompanies.value = await getOutreachDashboardCompanies(store.state.auth.token, dashboardParams())
    }
  } catch (e) {
    $toast.error('Failed to load dashboard summary')
    console.error('Dashboard fetch error:', e)
  } finally {
    dashboardLoading.value = false
  }
}

async function fetchEmailsSent() {
  emailsSentLoading.value = true
  try {
    if (dashboardScope.value === 'outreach') {
      emailsSentSummary.value = await getOutreachDashboardEmailsSentOutreachScoped(store.state.auth.token, dashboardParams())
    } else {
      emailsSentSummary.value = await getOutreachDashboardEmailsSent(store.state.auth.token, dashboardParams())
    }
  } catch (e) {
    $toast.error('Failed to load emails sent')
    console.error('Emails sent fetch error:', e)
  } finally {
    emailsSentLoading.value = false
  }
}

async function fetchActions() {
  actionsLoading.value = true
  try {
    const data = await getOutreachDashboardActions(store.state.auth.token, dashboardParams())
    console.log('fetchActions received data:', data)
    actionsData.value = data
  } catch (e) {
    console.error('Failed to fetch actions:', e)
    $toast.error('Failed to load next actions')
  } finally {
    actionsLoading.value = false
  }
}

async function fetchEmailFlow() {
  emailFlowLoading.value = true
  try {
    const data = await getOutreachDashboardEmailFlow(store.state.auth.token, dashboardParams())
    emailFlowEdges.value = data
  } catch (e) {
    console.error('Failed to fetch email flow:', e)
    $toast.error('Failed to load email flow')
  } finally {
    emailFlowLoading.value = false
  }
}

async function fetchTrendData() {
  trendChartLoading.value = true
  try {
    const data = await getOutreachDashboardCRMActionHistory(store.state.auth.token, dashboardParams())
    trendCompanyData.value = data

    // Find the earliest action date across all companies
    let earliestDate = null
    data.forEach((company) => {
      company.action_events?.forEach(([dateStr]) => {
        if (!earliestDate || dateStr < earliestDate) {
          earliestDate = dateStr
        }
      })
    })

    if (earliestDate) {
      trendStartDate.value = earliestDate
    }
    trendEndDate.value = new Date().toISOString().split('T')[0]
  } catch (e) {
    console.error('Failed to fetch trend data:', e)
    $toast.error(`Failed to load trend data: ${e.response?.statusText || e.message}`)
  } finally {
    trendChartLoading.value = false
  }
}

// Watch actionsData and render chart when it changes
watch(() => actionsData.value?.open_actions, async (newActions) => {
  if (newActions && newActions.length > 0) {
    await nextTick()
    await nextTick()  // Double tick to ensure DOM is fully updated
    renderActionsChart()
  }
})

// Computed: summary totals
const totalCompanies = computed(() => dashboardCompanies.value?.length ?? 0)
const companiesContacted = computed(() => dashboardCompanies.value?.filter((c) => c.contacted).length ?? 0)
const companiesResponded = computed(() => dashboardCompanies.value?.filter((c) => c.responded).length ?? 0)
const notInterestedCount = computed(() => dashboardCompanies.value?.filter((c) => c.not_interested).length ?? 0)

// Computed: response rates
const respondedRatePct = computed(() => {
  if (!dashboardCompanies.value || !companiesContacted.value) return null
  return Math.round((companiesResponded.value / companiesContacted.value) * 1000) / 10
})

const notInterestedBase = computed(() =>
  dashboardScope.value === 'outreach' ? companiesContacted.value : totalCompanies.value
)

const notInterestedRatePct = computed(() => {
  if (!dashboardCompanies.value || !notInterestedBase.value) return null
  return Math.round((notInterestedCount.value / notInterestedBase.value) * 1000) / 10
})

// Computed: company details modal
const companiesForModal = computed(() => {
  if (!dashboardCompanies.value || !companyDetailsBucket.value) return []

  let filtered = []
  if (companyDetailsBucket.value === 'total') {
    filtered = dashboardCompanies.value
  } else if (companyDetailsBucket.value === 'contacted') {
    filtered = dashboardCompanies.value.filter(c => c.contacted)
  } else if (companyDetailsBucket.value === 'responded') {
    filtered = dashboardCompanies.value.filter(c => c.responded)
  } else if (companyDetailsBucket.value === 'not_interested') {
    filtered = dashboardCompanies.value.filter(c => c.not_interested)
  }

  // Apply filter
  if (companyDetailsFilter.value) {
    filtered = filtered.filter(c => c.company_name.toLowerCase().includes(companyDetailsFilter.value.toLowerCase()))
  }

  // Apply sort
  const sorted = [...filtered]
  if (companyDetailsSortBy.value === 'company_name_asc') {
    sorted.sort((a, b) => a.company_name.localeCompare(b.company_name))
  } else if (companyDetailsSortBy.value === 'company_name_desc') {
    sorted.sort((a, b) => b.company_name.localeCompare(a.company_name))
  }

  return sorted
})

const bucketTitle = computed(() => {
  const titles = {
    total: 'All Companies',
    contacted: 'Companies Contacted',
    responded: 'Companies Responded',
    not_interested: 'Not Interested/Pass'
  }
  return titles[companyDetailsBucket.value] || ''
})

const bucketCount = computed(() => {
  const bucket = companyDetailsBucket.value
  if (bucket === 'total') return totalCompanies.value
  if (bucket === 'contacted') return companiesContacted.value
  if (bucket === 'responded') return companiesResponded.value
  if (bucket === 'not_interested') return notInterestedCount.value
  return 0
})

function openCompanyDetailsModal(bucket) {
  companyDetailsBucket.value = bucket
  companyDetailsFilter.value = ''
  companyDetailsSortBy.value = 'company_name_asc'
  showCompanyDetailsModal.value = true
}

// Computed: emails sent chart
const emailsSentTotal = computed(() =>
  EMAIL_STAGES.reduce((sum, stage) => sum + (emailsSentSummary.value?.[stage.name] || 0), 0)
)

const emailsSentChartData = computed(() => ({
  labels: EMAIL_STAGES.map(s => s.name),
  datasets: [{
    data: EMAIL_STAGES.map((stage) => emailsSentSummary.value?.[stage.name] || 0),
    backgroundColor: EMAIL_STAGES.map(s => s.color),
  }],
}))

const emailsSentChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
    centerText: {
      display: true,
      text: `${emailsSentTotal.value}`,
      fontSize: 24,
      color: '#25274D',
      fontFamily: 'Poppins',
    },
    datalabels: {
      color: 'white',
      font: { weight: 'bold', size: 11 },
      formatter: (value) => (value > 0 ? value : null),
    },
  },
}))

// Computed: next actions
const openActionsTotal = computed(() => actionsData.value?.open_actions?.reduce((sum, a) => sum + a.count, 0) ?? 0)
const overdueActionsCount = computed(() => actionsData.value?.overdue_actions?.length ?? 0)

// Computed: overdue modal options
const uniqueOverdueCompanies = computed(() => {
  const companies = new Set(actionsData.value?.overdue_actions?.map(a => a.company_name) ?? [])
  return Array.from(companies).sort()
})

const uniqueOverdueActions = computed(() => {
  const actions = new Set(actionsData.value?.overdue_actions?.map(a => a.action) ?? [])
  return Array.from(actions).sort()
})

// Computed: filtered overdue actions with sorting
const filteredOverdueActions = computed(() => {
  let filtered = actionsData.value?.overdue_actions ?? []
  if (overdueCompanyFilter.value) {
    filtered = filtered.filter(a => a.company_name === overdueCompanyFilter.value)
  }
  if (overdueActionFilter.value) {
    filtered = filtered.filter(a => a.action === overdueActionFilter.value)
  }

  const sorted = [...filtered]
  switch (overdueSortBy.value) {
    case 'days_overdue_asc':
      sorted.sort((a, b) => a.days_overdue - b.days_overdue)
      break
    case 'company_name_asc':
      sorted.sort((a, b) => a.company_name.localeCompare(b.company_name))
      break
    case 'company_name_desc':
      sorted.sort((a, b) => b.company_name.localeCompare(a.company_name))
      break
    case 'days_overdue_desc':
    default:
      sorted.sort((a, b) => b.days_overdue - a.days_overdue)
  }

  return sorted
})

// Computed: combined loading state
const isLoading = computed(() => dashboardLoading.value || emailsSentLoading.value || actionsLoading.value || emailFlowLoading.value)

// CONSTANTS: RESPONDED/NOT INTERESTED STATUSES
const RESPONDED_STATUSES = [
  'Cold Call Success', 'Responded / Coverage', 'Responded/Coverage', 'Initial Response', 'Additional Response', 'Client Response',
  'Received Email', 'Received LinkedIn DM', 'Shedd Call/Zoom Attended', 'Client Call/Zoom Attended',
  'Client Call/Zoom Scheduled', 'Shedd Call/Zoom Scheduled', 'Meeting Scheduled', 'Meeting Attended',
  'NDA Sent for Review', 'Followed up on NDA', 'NDA Executed', 'Received Materials',
  'Active - High Priority', 'Active High Priority', 'Active - In Dialogue', 'Other: Active - In Dialogue',
  'IOI Submitted', 'LOI Submitted', 'LOI Executed', 'Under LOI'
]

const NOT_INTERESTED_STATUSES = ['Hard No', 'Client Pass', 'Closed', 'Dead - Acquired by Other Party', 'Dead - Out of Business']

// CONSTANTS: COLORS
const statusColors = {
  'Not Contacted': '#a3a29c',
  'Email 1': '#86b6ef',
  'Email 2': '#5598e7',
  'Email 3': '#2a78d6',
  'Email 4': '#1c5cab',
  'Responded': '#008300',
  'Not Interested': '#e34948',
}

const emailFlowNodeColors = computed(() => ({
  'Email 1': '#86b6ef',
  'Email 2': '#5598e7',
  'Email 3': '#2a78d6',
  'Email 4': '#1c5cab',
  'Email Sent': '#f59e0b',
  'Responded': '#008300',
  'Not Interested': '#dc2626',
  'No Response': '#9ca3af',
}))

// CONSTANTS: FORMATTING
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const statusFilterSummary = computed(() => {
  if (activeStatuses.value.length === stackingOrder.length) return 'All statuses'
  if (activeStatuses.value.length === 0) return 'No statuses'
  return `${activeStatuses.value.length} selected`
})

const todayStr = computed(() => new Date().toISOString().split('T')[0])

function toggleStatusFilter() {
  showStatusFilterMenu.value = !showStatusFilterMenu.value
}

function initTrendDates() {
  if (trendStartDate.value || trendEndDate.value) return
  const today = new Date()
  const sixMonthsAgo = new Date(today)
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
  trendEndDate.value = today.toISOString().split('T')[0]
  trendStartDate.value = sixMonthsAgo.toISOString().split('T')[0]
}

function classifyCompanyAt(company, cutoffStr) {
  const campaignJoinDate = company.campaign_join_date
  if (campaignJoinDate && cutoffStr < campaignJoinDate) {
    return null
  }

  const actionEvents = company.action_events || []
  const statusEvents = company.status_events || []
  const emailStageOrder = Object.fromEntries(EMAIL_STAGES.map((s, i) => [s.name, i]))

  // Find most recent action up to cutoff
  let lastActionDate = null, furthestStage = -1, hasNotInterested = false, hasResponded = false
  for (const [date, action] of actionEvents) {
    if (date <= cutoffStr) {
      lastActionDate = date
      if (action === 'Not Interested') hasNotInterested = true
      else if (action === 'Responded') hasResponded = true
      else if (action in emailStageOrder) furthestStage = Math.max(furthestStage, emailStageOrder[action])
    }
  }

  // Find most recent status up to cutoff
  let lastStatusDate = null, lastStatusValue = null
  for (const [date, status] of statusEvents) {
    if (date <= cutoffStr) {
      lastStatusDate = date
      lastStatusValue = status
    }
  }

  if (RESPONDED_STATUSES.includes(lastStatusValue) && (!lastActionDate || lastStatusDate >= lastActionDate)) {
    return 'Responded'
  }
  if (NOT_INTERESTED_STATUSES.includes(lastStatusValue) && (!lastActionDate || lastStatusDate >= lastActionDate)) {
    return 'Not Interested'
  }

  if (hasNotInterested) return 'Not Interested'
  if (hasResponded) return 'Responded'
  if (furthestStage >= 0) return ['Email 1', 'Email 2', 'Email 3', 'Email 4'][furthestStage]
  if (lastActionDate || lastStatusDate) return 'Not Contacted'
  return null
}

function computeCensus(cutoffStr, activeStatusList) {
  const counts = {}
  activeStatusList.forEach(s => { counts[s] = 0 })

  trendCompanyData.value.forEach((company) => {
    const status = classifyCompanyAt(company, cutoffStr)
    if (status && activeStatusList.includes(status)) counts[status]++
  })
  return counts
}

function formatTrendLabel(d, interval) {
  return interval === 'Month'
    ? `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`
    : `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`
}

function generateCutoffDates(startStr, endStr, interval) {
  const start = new Date(startStr + 'T00:00:00Z')
  const end = new Date(endStr + 'T00:00:00Z')
  const dates = []
  if (end < start) return dates

  if (interval === 'Month') {
    let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1))
    while (cursor <= end) {
      const monthEnd = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0))
      dates.push(monthEnd < end ? monthEnd : end)
      cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1))
    }
  } else {
    const stepDays = interval === 'Day' ? 1 : 7
    let cursor = new Date(start)
    while (cursor < end) {
      dates.push(new Date(cursor))
      cursor = new Date(cursor.getTime() + stepDays * 86400000)
    }
    dates.push(new Date(end))
  }
  return dates
}

async function buildTrendPeriods() {
  if (!trendStartDate.value || !trendEndDate.value) {
    trendPeriods.value = []
    return
  }

  const cutoffDates = generateCutoffDates(trendStartDate.value, trendEndDate.value, trendInterval.value)
  const periods = cutoffDates.map((d) => {
    const cutoffStr = d.toISOString().split('T')[0]
    return {
      label: formatTrendLabel(d, trendInterval.value),
      ...computeCensus(cutoffStr, activeStatuses.value)
    }
  })

  trendPeriods.value = periods

  // Wait for DOM to update before rendering chart
  await nextTick()
  await nextTick()
  renderTrendAreaChart()
}

function expandStep(xs, ys) {
  const pts = [[xs[0], ys[0]]]
  for (let i = 1; i < xs.length; i++) {
    pts.push([xs[i], ys[i - 1]])
    pts.push([xs[i], ys[i]])
  }
  return pts
}

function stepBandPath(xs, topYs, bottomYs) {
  const top = expandStep(xs, topYs)
  const bottom = expandStep(xs, bottomYs).reverse()
  return 'M' + top.concat(bottom).map((p) => p.join(',')).join(' L') + ' Z'
}

function svgEl(tag, attrs) {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag)
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
  return el
}

function renderTrendAreaChart() {
  const svg = document.getElementById('trend-area-chart')
  if (!svg) return

  svg.innerHTML = ''

  const chartW = 800, chartH = 320
  const marginLeft = 40, marginRight = 12, marginTop = 26, marginBottom = 40
  const plotW = chartW - marginLeft - marginRight
  const plotH = chartH - marginTop - marginBottom
  const baselineY = marginTop + plotH - 1  // Start at bottom
  const tickStep = 10

  if (trendPeriods.value.length === 0) {
    const empty = svgEl('text', {
      x: chartW / 2, y: chartH / 2, 'text-anchor': 'middle', 'font-size': 12, fill: '#9ca3af',
    })
    empty.textContent = 'No data for this date range'
    svg.appendChild(empty)
    return
  }

  const activeCats = stackingOrder.filter(cat => activeStatuses.value.includes(cat))
  const periodTotals = trendPeriods.value.map((p) =>
    activeCats.reduce((sum, cat) => sum + (p[cat] || 0), 0)
  )
  const maxTotal = Math.max(0, ...periodTotals)
  const yMax = Math.max(tickStep, Math.ceil(maxTotal / tickStep) * tickStep)
  const scale = plotH / yMax

  for (let g = 0; g <= yMax; g += tickStep) {
    const y = baselineY - g * scale
    svg.appendChild(svgEl('line', {
      x1: marginLeft, x2: chartW - marginRight, y1: y, y2: y,
      stroke: g === 0 ? '#d1d5db' : '#e5e7eb',
      'stroke-width': 1,
    }))
    const label = svgEl('text', {
      x: marginLeft - 8, y: y + 3, 'text-anchor': 'end', 'font-size': 10, fill: '#9ca3af',
    })
    label.textContent = g.toLocaleString()
    svg.appendChild(label)
  }

  const n = trendPeriods.value.length
  const xs = trendPeriods.value.map((p, i) => marginLeft + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW))

  const cumByPeriod = trendPeriods.value.map((period, pi) => {
    const cum = [0]
    activeCats.forEach((cat) => {
      cum.push(cum[cum.length - 1] + (period[cat] || 0))
    })
    return cum
  })

  activeCats.forEach((cat, ci) => {
    const topYs = trendPeriods.value.map((p, i) => baselineY - cumByPeriod[i][ci + 1] * scale)
    const bottomYs = trendPeriods.value.map((p, i) => baselineY - cumByPeriod[i][ci] * scale)
    const path = svgEl('path', {
      d: stepBandPath(xs, topYs, bottomYs),
      fill: statusColors[cat],
      'fill-opacity': 0.72,
    })
    svg.appendChild(path)

    // Top-edge stroke
    const topPts = expandStep(xs, topYs)
    svg.appendChild(svgEl('polyline', {
      points: topPts.map((p) => p.join(',')).join(' '),
      fill: 'none',
      stroke: '#ffffff',
      'stroke-width': 1.5,
    }))
  })

  if (trendInterval.value !== 'Day') {
    trendPeriods.value.forEach((p, i) => {
      svg.appendChild(svgEl('line', {
        x1: xs[i], x2: xs[i], y1: baselineY, y2: baselineY + 4,
        stroke: '#d1d5db', 'stroke-width': 1,
      }))
    })
  }

  let labelInterval = 1
  if (n > 20) labelInterval = Math.ceil(n / 10)
  else if (n > 12) labelInterval = Math.ceil(n / 6)
  else if (n > 6) labelInterval = 2

  const labelIndices = new Set()
  for (let i = 0; i < n; i += labelInterval) {
    labelIndices.add(i)
  }
  labelIndices.add(n - 1)

  labelIndices.forEach(i => {
    const p = trendPeriods.value[i]
    const x = xs[i]
    const label = svgEl('text', {
      x, y: baselineY + 20, 'text-anchor': 'middle', 'font-size': 11, fill: '#6b7280',
    })
    label.textContent = p.label
    svg.appendChild(label)
  })
}

watch([trendCompanyData, trendStartDate, trendEndDate, trendInterval, activeStatuses], async () => {
  await buildTrendPeriods()
}, { deep: true })

// Re-fetch dashboard data when scope changes
watch(dashboardScope, async () => {
  if (dashboardCompanies.value !== null || emailsSentSummary.value !== null) {
    fetchDashboardCompanies()
    fetchEmailsSent()
  }
})

onMounted(() => {
  initTrendDates()
  buildTrendPeriods()

  // Close status filter menu when clicking outside
  const closeMenu = (e) => {
    const filterEl = document.querySelector('[data-trend-status-filter]')
    if (filterEl && !filterEl.contains(e.target)) {
      showStatusFilterMenu.value = false
    }
  }
  document.addEventListener('click', closeMenu)
  return () => document.removeEventListener('click', closeMenu)
})

// Render the next actions chart (SVG)
function renderActionsChart() {
  const svg = document.getElementById('next-actions-chart')
  if (!svg || !actionsData.value?.open_actions) return

  svg.innerHTML = ''

  const actions = actionsData.value.open_actions
  const nextStepsActions = actions.filter(a => a.type === 'next_steps')
  const initialOutreachActions = actions.filter(a => a.type === 'initial_outreach')

  const w = 640
  const labelX = 180, rightMargin = 40
  const plotLeft = labelX + 10, plotRight = w - rightMargin
  const plotW = plotRight - plotLeft
  const topMargin = 10, bottomMargin = 20
  const rowH = 24
  const barH = 12
  const tickMax = Math.ceil(Math.max(...actions.map(a => a.count ?? a.value ?? 0)) / 5) * 5
  const tickStep = tickMax <= 15 ? 5 : Math.ceil(tickMax / 3)
  const scale = plotW / tickMax

  const colorMap = {
    next_steps: '#1c5cab',
    initial_outreach: '#86b6ef'
  }

  // Calculate dynamic height based on content
  let contentHeight = topMargin
  if (nextStepsActions.length > 0) {
    contentHeight += 16 + (nextStepsActions.length * rowH) + 6
  }
  if (initialOutreachActions.length > 0) {
    contentHeight += 16 + (initialOutreachActions.length * rowH)
  }
  const h = contentHeight + bottomMargin

  svg.setAttribute('viewBox', `0 0 ${w} ${h}`)

  // Gridlines and tick labels
  for (let g = 0; g <= tickMax; g += tickStep) {
    const x = plotLeft + g * scale
    const line = svgEl('line', {
      x1: x, x2: x, y1: topMargin, y2: h - bottomMargin,
      stroke: g === 0 ? '#d1d5db' : '#e5e7eb', 'stroke-width': '1',
    })
    svg.appendChild(line)

    const text = svgEl('text', {
      x, y: h - 6, 'text-anchor': 'middle', 'font-size': '10', fill: '#9ca3af',
    })
    text.textContent = g.toLocaleString()
    svg.appendChild(text)
  }

  let currentY = topMargin + 12

  // Next Steps Section
  if (nextStepsActions.length > 0) {
    const sectionTitle = svgEl('text', {
      x: 10, y: currentY, 'font-size': '11', 'font-weight': '700', fill: '#6b7280',
    })
    sectionTitle.textContent = 'Next Steps'
    svg.appendChild(sectionTitle)
    currentY += 16

    nextStepsActions.forEach((item) => {
      renderActionBar(svg, item, currentY, plotLeft, rowH, barH, scale, labelX, colorMap)
      currentY += rowH
    })

    if (initialOutreachActions.length > 0) {
      currentY += 6
    }
  }

  // Initial Outreach Section
  if (initialOutreachActions.length > 0) {
    if (nextStepsActions.length > 0) {
      const sectionTitle = svgEl('text', {
        x: 10, y: currentY, 'font-size': '11', 'font-weight': '700', fill: '#6b7280',
      })
      sectionTitle.textContent = 'Initial Outreach'
      svg.appendChild(sectionTitle)
      currentY += 16
    }

    initialOutreachActions.forEach((item) => {
      renderActionBar(svg, item, currentY, plotLeft, rowH, barH, scale, labelX, colorMap)
      currentY += rowH
    })
  }
}

function renderActionBar(svg, item, centerY, plotLeft, rowH, barH, scale, labelX, colorMap) {
  const barColor = colorMap[item.type] || colorMap.initial_outreach
  const count = item.count ?? item.value ?? 0
  const barW = count * scale

  const label = svgEl('text', {
    x: labelX, y: centerY + 4, 'text-anchor': 'end', 'font-size': '12', fill: '#1f2937',
  })
  label.textContent = item.action || item.label || item.next_action || 'Unknown'
  svg.appendChild(label)

  const rect = svgEl('rect', {
    x: plotLeft, y: centerY - barH / 2, width: barW, height: barH, fill: barColor,
  })
  svg.appendChild(rect)

  const value = svgEl('text', {
    x: plotLeft + barW + 6, y: centerY + 4, 'text-anchor': 'start',
    'font-size': '11', 'font-weight': '650', fill: '#1f2937',
  })
  value.textContent = count.toLocaleString()
  svg.appendChild(value)
}

</script>

<style scoped>
/* Shrink the selected pills so they fit within the input row */
.filter-typeahead :deep(.bg-indigo-200) {
  font-size: 0.7rem;
  line-height: 1rem;
  padding-top: 1px;
  padding-bottom: 1px;
  margin-top: 2px;
  margin-bottom: 2px;
}
</style>
